// Part 2 — feature-reveal walkthrough.
//
// A spotlight tour of 10 stops (see revealStops.js). Each stop DEEP-LINKS:
// changing stop navigates the app so the operator sees the real screen
// behind the dim. Next is always enabled here — these are "Got it"
// moments, not tasks — with Back, progress dots, and an always-visible
// "Skip tour". Position is persisted by the parent so it resumes
// cross-device. On Next past the last stop, we hand off to the AI finale.

import React from 'react';
import Spotlight from './Spotlight';
import { REVEAL_STOPS } from './revealStops';
import { clampReveal, isLastReveal, REVEAL_COUNT } from './tourLogic';

function ProgressDots({ index, total }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4 bg-violet-500' : i < index ? 'w-1.5 bg-violet-300' : 'w-1.5 bg-slate-200'}`}
        />
      ))}
    </div>
  );
}

export default function FeatureReveals({ index, onNavigate, onIndexChange, onFinish, onSkip }) {
  const idx = clampReveal(index, REVEAL_COUNT);
  const stop = REVEAL_STOPS[idx];

  // Deep-link to the current stop's surface whenever the stop changes.
  React.useEffect(() => {
    if (stop && typeof onNavigate === 'function') onNavigate(stop);
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  const goNext = () => {
    if (isLastReveal(idx, REVEAL_COUNT)) { onFinish(); return; }
    onIndexChange(idx + 1);
  };
  const goBack = () => { if (idx > 0) onIndexChange(idx - 1); };

  if (!stop) return null;

  return (
    <Spotlight anchors={stop.anchors}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-violet-600">
            {idx + 1} of {REVEAL_COUNT} · {stop.title}
          </span>
          <button onClick={onSkip} className="text-[11px] text-slate-400 hover:text-slate-700 font-medium">Skip tour</button>
        </div>
        <p className="text-sm text-slate-800 leading-snug">{stop.copy}</p>
        <div className="flex items-center justify-between mt-4">
          <ProgressDots index={idx} total={REVEAL_COUNT} />
          <div className="flex items-center gap-2">
            <button
              onClick={goBack}
              disabled={idx === 0}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${idx === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              ← Back
            </button>
            <button
              onClick={goNext}
              className="bg-violet-600 hover:bg-violet-700 text-white font-bold text-xs px-4 py-1.5 rounded-lg transition-colors"
            >
              {isLastReveal(idx, REVEAL_COUNT) ? 'Meet your AI →' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </Spotlight>
  );
}
